import { StageDetailDialog } from '@/components/common/StageDetailDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { isQueueFieldEditable } from './exception-detail/helpers';
import {
  ActivityCard,
  ApprovalDraftPrompt,
  DuplicateReviewDialog,
  ExceptionStickyHeader,
  FeedbackDialog,
  FieldTileGrid,
  ForfeitDialog,
  LineItemsTable,
  OrderProgressCard,
  OrderSourceCard,
  TimelineContextMenu,
  WarningsBanners,
} from './exception-detail/components';
import { useExceptionDetail } from './useExceptionDetail';
import { operatorSaveErrorMessage } from './exception-detail/helpers/operator-save-error-message';
import { ManualLinesEditor } from './exception-detail/components/ManualLinesEditor';

export function ExceptionDetailPage(): JSX.Element {
  const state = useExceptionDetail();

  if (state.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (state.isError || !state.data) {
    return (
      <Card className="overflow-visible">
        <CardContent className="flex items-center justify-between gap-4 py-8">
          <div className="text-sm text-muted-foreground">
            Failed to load the exception.{' '}
            {state.error instanceof Error ? state.error.message : 'Unknown error.'}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void state.refetch()}
            disabled={state.isFetching}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { data } = state;

  // ADO #700: assemble the reason-controls bag once so FieldTileGrid and
  // LineItemsTable get the same instance. Keep `openReasonKey` and the Set
  // of missing keys derived here so we don't re-derive in every child.
  const reasonControls = {
    fieldReasons: state.fieldReasons,
    fieldReasonDrafts: state.fieldReasonDrafts,
    missingReasonKeys: new Set(state.missingReasons.map((field) => field.reasonKey)),
    openReasonKey: state.reasonPopover?.reasonKey ?? null,
    requestReasonImmediate: state.requestReasonImmediate,
    requestReasonDebounced: state.requestReasonDebounced,
    dismissReasonPopover: state.dismissReasonPopover,
    setReasonDraft: state.setReasonDraft,
    commitReason: state.commitReason,
  };

  return (
    <div className="flex flex-col gap-4">
      <ExceptionStickyHeader
        state={data.state}
        lastTransitionedBy={data.lastTransitionedBy}
        lastTransitionedAt={data.lastTransitionedAt}
        feedbackPresent={state.feedback.trim().length > 0}
        isDuplicateReview={state.isDuplicateReview}
        isDraftRejectedReview={state.isDraftRejectedReview}
        canForceAdvance={state.canApproveCurrentMatches}
        hasResolutionChanges={state.hasResolutionChanges}
        saveResolutionPending={state.saveResolution.isPending}
        missingReasonLabels={state.missingReasons.map((field) => field.label)}
        duplicateDecisionPending={state.duplicateDecision.isPending}
        canDelete={state.canDelete}
        deleteQueueId={state.id}
        onBack={() => state.navigate('/queue')}
        onSave={state.runSave}
        onResolveDuplicate={() => state.setDuplicateDecisionOpen(true)}
        onForfeit={() => state.setForfeitOpen(true)}
        onOpenFeedback={() => state.setFeedbackOpen(true)}
        onDeleted={() => state.navigate('/queue')}
      />

      {!state.feedbackOpen && (state.saveResolution.error || state.submit.error) && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm"
        >
          <p className="font-medium">Changes were not saved</p>
          <p className="mt-1 text-muted-foreground">
            {operatorSaveErrorMessage(state.saveResolution.error ?? state.submit.error)}
          </p>
        </div>
      )}

      <WarningsBanners
        obstructive={state.warningGroups.obstructive}
        informative={state.warningGroups.informative}
        fallback={state.warningGroups.fallback}
      />

      <OrderProgressCard
        currentStatus={data.state}
        currentStageKey={state.currentDisplayedStageKey}
        currentStageIndex={state.currentStageIndex}
        stageDetails={data.stageDetails}
        selectedStageKey={state.selectedStageKey}
        selectedSubstep={state.selectedSubstep}
        activeTimelineSteps={state.activeTimelineSteps}
        isApprovalDraftWaiting={state.isApprovalDraftWaiting}
        showSubsteps={state.showPipelineSubsteps}
        onToggleSubsteps={() => state.setShowPipelineSubsteps((current) => !current)}
        onSelectStage={(stageKey) => {
          state.setSelectedStageKey(stageKey);
          state.setSelectedSubstep(null);
        }}
        onSelectSubstep={(stageKey, substep) => {
          state.setSelectedStageKey(stageKey);
          state.setSelectedSubstep(substep);
        }}
        onApprovalReopen={() => state.setApprovalPromptDismissed(false)}
        onStageContextMenu={state.openStageContextMenu}
      />

      <Card>
        <CardHeader>
          <CardTitle>Customer Purchase Order</CardTitle>
          <CardDescription>Check the order details and matched values.</CardDescription>
        </CardHeader>
        <CardContent>
          <FieldTileGrid
            fields={data.fields}
            fieldValues={state.fields}
            onFieldChange={(key, value) => state.setFields((prev) => ({ ...prev, [key]: value }))}
            highlightedErrorFields={state.highlightedErrorFields}
            isFieldEditable={(field) => isQueueFieldEditable(field, state.queueItemReadOnly)}
            customerPicker={{
              searchValue: state.customerSearch,
              debouncedSearchValue: state.debouncedCustomerSearch,
              onSearchChange: (value, role) => {
                state.setCustomerSearchRole(role);
                state.setCustomerSearch(value);
              },
              catalogOptions: state.customerSearchResults.data ?? [],
              relatedBillToSelection: state.relatedBillToSelection,
              onCustomerAccountSelect: (role, option) => {
                if (role === 'bill_to') {
                  state.setRelatedBillToSelection(null);
                  return;
                }
                if (option.invoiceAccount) {
                  const address = option.invoiceAddress ?? option.address;
                  state.setRelatedBillToSelection({
                    id: `bill-to:${option.invoiceAccount}`,
                    customerNumber: option.invoiceAccount,
                    name: 'Invoice Account',
                    display: `${option.invoiceAccount} - Invoice Account for ${option.customerNumber}`,
                    role: 'bill_to',
                    ...(address ? { address } : {}),
                    ...(option.isStoppedAll !== undefined
                      ? { isStoppedAll: option.isStoppedAll }
                      : {}),
                  });
                } else {
                  state.setRelatedBillToSelection({
                    ...option,
                    id: `bill-to:${option.customerNumber}`,
                    role: 'bill_to',
                    ...(option.invoiceAddress ? { address: option.invoiceAddress } : {}),
                  });
                }
              },
            }}
            reasonControls={reasonControls}
          />
          <div id="manual-lines-editor">
            <ManualLinesEditor lines={state.manualLines} unitOptions={data.unitOptions}
              disabled={state.queueItemReadOnly || state.saveResolution.isPending}
              showErrors={state.showManualLineErrors} saveError={state.manualLineSaveError}
              onAdd={state.addManualLine} onChange={state.updateManualLine} onRemove={state.removeManualLine}
              productPicker={{
                productPopoverLine: state.productPopoverLine, onProductPopoverLineChange: state.setProductPopoverLine,
                searchValue: state.productSearch, debouncedSearchValue: state.debouncedProductSearch,
                onSearchChange: state.setProductSearch, searchResults: state.productSearchResults.data ?? [],
              }} />
          </div>
          <LineItemsTable
            lineItems={data.lineItems}
            unitOptions={data.unitOptions}
            disabled={state.queueItemReadOnly}
            productPicker={{
              productPopoverLine: state.productPopoverLine,
              onProductPopoverLineChange: state.setProductPopoverLine,
              searchValue: state.productSearch,
              debouncedSearchValue: state.debouncedProductSearch,
              onSearchChange: state.setProductSearch,
              searchResults: state.productSearchResults.data ?? [],
              selectedProducts: state.selectedProducts,
              onProductSelect: (lineNumber, itemNumber) =>
                state.setSelectedProducts((prev) => ({ ...prev, [lineNumber]: itemNumber })),
            }}
            selectedQuantities={state.selectedQuantities}
            excludedLines={state.excludedLines}
            onCommitLineExclusion={state.commitLineExclusion}
            onRemoveLineExclusion={state.removeLineExclusion}
            onQuantityChange={(lineNumber, value) =>
              state.setSelectedQuantities((prev) => ({ ...prev, [lineNumber]: value }))
            }
            selectedUnits={state.selectedUnits}
            onUnitChange={(lineNumber, value) =>
              state.setSelectedUnits((prev) => ({ ...prev, [lineNumber]: value }))
            }
            reasonControls={reasonControls}
          />
        </CardContent>
      </Card>

      <div>
        <OrderSourceCard data={data} extractedCustomerContext={state.extractedCustomerContext} />
      </div>

      <ActivityCard
        executionLogs={data.executionLogs}
        showAllExecutionLogs={state.showAllExecutionLogs}
        onShowAllExecutionLogs={() => state.setShowAllExecutionLogs(true)}
      />

      <FeedbackDialog
        open={state.feedbackOpen}
        onOpenChange={state.setFeedbackOpen}
        feedback={state.feedback}
        onFeedbackChange={state.setFeedback}
        canSubmit={state.canSubmit}
        submitPending={state.submit.isPending || state.saveResolution.isPending}
        errorMessage={
          state.saveResolution.error || state.submit.error
            ? operatorSaveErrorMessage(state.saveResolution.error ?? state.submit.error)
            : undefined
        }
        onResume={() => state.onAction('resume')}
        onClose={() => state.onAction('close')}
      />
      <ApprovalDraftPrompt
        open={state.showApprovalDraftPrompt}
        onDismiss={() => state.setApprovalPromptDismissed(true)}
        onOpenDraft={() => state.navigate(`/drafts?draft=${encodeURIComponent(data.id)}`)}
      />
      <ForfeitDialog
        open={state.forfeitOpen}
        onOpenChange={state.setForfeitOpen}
        reason={state.forfeitReason}
        onReasonChange={state.setForfeitReason}
        skipAcknowledgement={state.skipForfeitAcknowledgement}
        onSkipAcknowledgementChange={state.setSkipForfeitAcknowledgement}
        pending={state.forfeitOrder.isPending}
        onConfirm={state.runForfeit}
      />
      <DuplicateReviewDialog
        open={state.duplicateDecisionOpen}
        onOpenChange={state.setDuplicateDecisionOpen}
        pending={state.duplicateDecision.isPending}
        onDuplicate={() => state.runDuplicateDecision('duplicate')}
        onNotDuplicate={() => state.runDuplicateDecision('not_duplicate')}
      />
      <StageDetailDialog
        detail={state.selectedStageDetail}
        open={state.selectedStageKey !== null}
        selectedSubstep={state.selectedSubstep}
        onOpenChange={(open) => {
          if (!open) {
            state.setSelectedStageKey(null);
            state.setSelectedSubstep(null);
          }
        }}
      />
      <TimelineContextMenu
        position={
          state.contextMenu
            ? {
                x: state.contextMenu.x,
                y: state.contextMenu.y,
                stageLabel: state.contextMenu.stageLabel,
                substepLabel: state.contextMenu.substepLabel,
              }
            : null
        }
        retryPending={state.stageRetry.isPending}
        onRetrySingle={() => state.runRetry('single')}
        onRetryFromHere={() => state.runRetry('from_here')}
      />
    </div>
  );
}
